import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { UserSheetFieldLabel } from '@/components/custom/admin/user-sheet'
import { OGSM_ACCESS_COPY, OGSM_ACCESS_LABELS } from '@/features/ogsm/access'
import type { OgsmAccess } from '@/services/portal.service'

const OGSM_ACCESS_OPTIONS: OgsmAccess[] = ['READ_ONLY', 'EDIT']

function isOgsmAccess(value: string): value is OgsmAccess {
  return value === 'READ_ONLY' || value === 'EDIT'
}

export interface OgsmAccessFieldProps {
  id: string
  value: OgsmAccess
  onChange: (value: OgsmAccess) => void
  /** Admins always edit: the field shows Edit + Read only and keeps `value` underneath. */
  locked: boolean
  /** Locks the field for another reason; this text replaces the helper. */
  lockReason?: string
  disabled?: boolean
  error?: string | null
}

export function OgsmAccessField({
  id,
  value,
  onChange,
  locked,
  lockReason,
  disabled = false,
  error,
}: OgsmAccessFieldProps) {
  const helperId = `${id}-helper`
  const errorId = `${id}-error`

  return (
    <div>
      <UserSheetFieldLabel htmlFor={id}>
        {OGSM_ACCESS_COPY.field}
      </UserSheetFieldLabel>
      <Select
        value={locked ? 'EDIT' : value}
        onValueChange={(next) => {
          if (isOgsmAccess(next)) onChange(next)
        }}
        disabled={locked || lockReason !== undefined || disabled}
      >
        <SelectTrigger
          id={id}
          className="h-10 border-primary/20"
          aria-describedby={error ? `${helperId} ${errorId}` : helperId}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OGSM_ACCESS_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {OGSM_ACCESS_LABELS[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Typography
        id={helperId}
        variant="body-sm"
        className="mt-1.5 text-primary/70"
      >
        {lockReason ??
          (locked ? OGSM_ACCESS_COPY.adminHelper : OGSM_ACCESS_COPY.helper)}
      </Typography>
      {error && (
        <Typography
          id={errorId}
          role="alert"
          variant="body-sm"
          className="mt-1.5 text-destructive"
        >
          {error}
        </Typography>
      )}
    </div>
  )
}
